/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { FlexContainer, theme, Text } from "@nordcloud/gnui";
import { QueryStateFilter, QueryStateFilterValue } from "~/hooks";
import { isLastIndex, isNotEmpty, isNotNil, noop } from "~/tools";
import { SearchV2Filters } from "../../types";
import { ActionButtons } from "./ActionButtons";
import { ActiveFilterItem } from "./ActiveFilterItem";
import {
  costBasedFilters,
  dateBasedFilters,
  MappedFilterName,
} from "./constants";
import { formatActiveFilterName } from "./helpers";

type Props = {
  filters: QueryStateFilter<SearchV2Filters>[];
  allowSavingFilters: boolean;
  onRemoveKey: (key: (keyof SearchV2Filters)[]) => void;
  onRemoveValue: (key: string, index: number) => void;
  onClear?: () => void;
  onSave?: () => void;
};

export function EstateActiveFiltersV2({
  filters,
  allowSavingFilters,
  onRemoveKey,
  onRemoveValue,
  onClear = noop,
  onSave = noop,
}: Props) {
  const handleRemoveKey = (key: string) => {
    switch (key) {
      case "waste":
        onRemoveKey(["wastePolicy", key]);
        break;
      case "enrichment":
        onRemoveKey(["enrichmentIntegrationId", key]);
        break;
      case "provider":
        onRemoveKey(["externalIntegration", "region", key]);
        break;
      case "discoveryTime":
        onRemoveKey(["firstSeenAtFrom", "firstSeenAtTo"]);
        break;
      case "lifeSpan":
        onRemoveKey(["lifeSpanFrom", "lifeSpanTo"]);
        break;
      case "monthlyCost":
        onRemoveKey(["costFrom", "costTo"]);
        break;
      case "unallocated":
        onRemoveKey([
          "assignmentUpdatedFrom",
          "assignmentUpdatedTo",
          "unassigned",
        ]);
        break;
      case "unassigned":
        onRemoveKey([
          "unassigned",
          "unassignedExclude",
          "assignmentUpdatedFrom",
          "assignmentUpdatedTo",
        ]);
        break;
      default:
        onRemoveKey([
          key as keyof SearchV2Filters,
          // remove exclude filter flag too
          key.toString().concat("Exclude") as keyof SearchV2Filters,
        ]);
        break;
    }
  };

  const excludedFilters = filters
    .filter(([key]) => isExcludedFilter(key))
    .map(([key]) => key.replace(/exclude/i, ""))
    .map((excluded) => {
      switch (excluded) {
        case "cost":
          return "monthlyCost";
        case "firstSeenAt":
          return "discoveryTime";
        case "unassigned":
          return "unallocated";
        default:
          return excluded;
      }
    });

  const remainingFilters = getRemainingFilters(filters);

  const showActions = isNotEmpty(remainingFilters);

  return (
    <FlexContainer wrap="wrap">
      {remainingFilters.map(([key, value], index) => (
        <FlexContainer
          key={`filter-${key}`}
          direction="column"
          mb={theme.spacing.spacing04}
          alignItems="baseline"
        >
          <Text
            size="sm"
            color={theme.color.text.text02}
            weight="medium"
            mb={theme.spacing.spacing00}
          >{`${formatActiveFilterName(key)}:`}</Text>
          <FlexContainer>
            <ActiveFilterItem
              key={key}
              name={key}
              value={value}
              excludedFilters={excludedFilters}
              onRemoveValue={onRemoveValue}
              onRemoveKey={() => handleRemoveKey(key)}
            />
            <When condition={!isLastIndex(remainingFilters, index)}>
              <Text
                size="sm"
                mb={0}
                mr={theme.spacing.spacing02}
                color={theme.color.text.text02}
                weight="medium"
              >
                &
              </Text>
            </When>
          </FlexContainer>
        </FlexContainer>
      ))}
      <FlexContainer>
        <When condition={showActions}>
          <ActionButtons
            allowSavingFilters={allowSavingFilters}
            onClear={onClear}
            onSave={onSave}
          />
        </When>
      </FlexContainer>
    </FlexContainer>
  );
}

function isExcludedFilter(key: keyof SearchV2Filters) {
  return key.toLowerCase().includes("exclude");
}

function getRemainingFilters(filters: QueryStateFilter<SearchV2Filters>[]) {
  const nonExcludedFilters = filters.filter(([key]) => !isExcludedFilter(key));

  const filterObjects = filters.map(([key, value]) => {
    return { [key]: value };
  });

  const { mappedFilter: discoveryFilter } = getFilterValues(
    "firstSeenAtFrom",
    "firstSeenAtTo",
    filterObjects
  );

  const { mappedFilter: lifeSpanFilter } = getFilterValues(
    "lifeSpanFrom",
    "lifeSpanTo",
    filterObjects
  );

  const { mappedFilter: costFilter } = getFilterValues(
    "costFrom",
    "costTo",
    filterObjects
  );

  const { mappedFilter: unallocatedFilter } = getFilterValues(
    "assignmentUpdatedFrom",
    "assignmentUpdatedTo",
    filterObjects
  );

  if (
    isNotNil(discoveryFilter) ||
    isNotNil(lifeSpanFilter) ||
    isNotNil(costFilter) ||
    isNotNil(unallocatedFilter)
  ) {
    const filteredFilters = nonExcludedFilters.filter(
      (item) => ![...dateBasedFilters, ...costBasedFilters].includes(item[0])
    );

    return [
      ...filteredFilters,
      discoveryFilter,
      lifeSpanFilter,
      costFilter,
      unallocatedFilter,
    ].filter(isNotNil);
  }

  return nonExcludedFilters;
}

function isFilterSelected(
  from: QueryStateFilter<SearchV2Filters> | string,
  to: QueryStateFilter<SearchV2Filters> | string
) {
  return isNotEmpty(from) || isNotEmpty(to);
}

function findFilterByName(
  name: string,
  filters: {
    [x: string]: QueryStateFilterValue;
  }[]
): string {
  return filters.find((item) => item[name])?.[name]?.toString() ?? "";
}

function getFilterValues(
  filterFrom: string,
  filterTo: string,
  filters: {
    [x: string]: QueryStateFilterValue;
  }[]
) {
  const valueFrom = findFilterByName(filterFrom, filters);
  const valueTo = findFilterByName(filterTo, filters);
  const isDefined = isFilterSelected(valueFrom, valueTo);
  const mappedFilter = isDefined
    ? [mapFilterName(filterFrom), `${valueFrom},${valueTo}`]
    : null;

  return {
    mappedFilter,
  };
}

function mapFilterName(filterName: string) {
  switch (filterName) {
    case "firstSeenAtFrom":
      return MappedFilterName.DISCOVERY;
    case "lifeSpanFrom":
      return MappedFilterName.LIFE_SPAN;
    case "costFrom":
      return MappedFilterName.MONTHLY_COST;
    case "assignmentUpdatedFrom":
      return MappedFilterName.UNALLOCATED;
    default:
      return "";
  }
}
